import React from 'react';

import moment from 'moment';
import type { Duration } from 'moment';
import classNames from 'classnames';
import { Tooltip, Progress } from '@bringg/react-components';
import { DeliveryBlock } from '@bringg/types';

import { RoutesPlannerRun } from '../../../../../../types';
import { getResourceDeliveryBlocks } from '../../../../../routes-planner/timeline-logic/delivery-block-logic';
import { dateUtils } from '../../../../../../utils/date-utils';
import { roundNumber } from '../../../../../../utils/numbers-utils';

import './duration-cell.scss';

interface Props {
	run: RoutesPlannerRun;
	deliveryBlocksByDriver: { [userId: number]: DeliveryBlock[] };
	deliveryBlocksByVehicle: { [vehicleId: number]: DeliveryBlock[] };
	initialLoadingTimeMinutes: number;
}

const DurationCell = ({ run, deliveryBlocksByDriver, deliveryBlocksByVehicle, initialLoadingTimeMinutes }: Props) => {
	const getReloadTime = (block: DeliveryBlock) => {
		if (block.delivery_block_recharges?.length) {
			let duration = 0;
			block.delivery_block_recharges.forEach(recharge => {
				if (dateUtils.isBetween(recharge.start_time, [block.start_time, block.end_time])) {
					duration += initialLoadingTimeMinutes;
				}
			});
			return duration;
		}
		return 0;
	};

	const getRunDuration = () => {
		const runStartTime = run.depot_etl;
		const runEndTime = run.depot_back_eta;
		const runDuration: Duration = moment.duration(moment(runEndTime * 1000).diff(moment(runStartTime * 1000)));
		const deliveryBlocks = getResourceDeliveryBlocks(
			run,
			deliveryBlocksByDriver,
			deliveryBlocksByVehicle,
			moment(runStartTime * 1000).toISOString(),
			moment(runEndTime * 1000).toISOString()
		);

		if (deliveryBlocks.length) {
			const deliveryBlockDuration: Duration = moment.duration();
			deliveryBlocks.forEach(block => {
				const rechargesTime = getReloadTime(block);
				const rechargesInMilliseconds = rechargesTime * 60000;

				const blockDuration: Duration = moment.duration(moment(block.end_time).diff(moment(block.start_time)));
				deliveryBlockDuration.add(blockDuration).subtract(rechargesInMilliseconds);
			});

			return { deliveryBlockDuration, runDuration };
		}
		return { runDuration };
	};

	const { runDuration, deliveryBlockDuration } = getRunDuration();
	const runOutOfBlock = deliveryBlockDuration
		? (runDuration.asMilliseconds() / deliveryBlockDuration.asMilliseconds()) * 100
		: 0;
	const tooltipText = deliveryBlockDuration ? `${roundNumber(runOutOfBlock)}%` : '';
	return (
		<div className={classNames('duration', { 'duration-no-bar': !deliveryBlockDuration })}>
			<Tooltip title={tooltipText}>
				{deliveryBlockDuration ? <Progress percent={runOutOfBlock} showInfo={false} /> : null}
			</Tooltip>
			<span>{`${moment.utc(runDuration.asMilliseconds()).format('HH:mm')}`}</span>
		</div>
	);
};

export default DurationCell;
